import { QueryClientProvider } from '@tanstack/react-query';
import TranslationsProvider from '@utils/i18n/i18n';
import { queryClient } from '@App/App';
import { createRoot } from 'react-dom/client';
import translator from '@App/app.translator';
import Popup from './Popup/Popup';

async function addMarkerImagesToMap(maplibreMap) {
  const imgFiles = import.meta.glob('/src/img/marker/providers/*.png');

  const markerImage = await maplibreMap.loadImage('/img/marker/sdf-marker.png');
  maplibreMap.addImage('sdf-marker', markerImage.data, { sdf: true });

  const counterImage = await maplibreMap.loadImage('/img/marker/counter.png');
  maplibreMap.addImage('counter', counterImage.data);

  const clusterImage = await maplibreMap.loadImage('/img/marker/sdf-cluster.png');
  maplibreMap.addImage('sdf-cluster', clusterImage.data, { sdf: true });

  await Promise.all(
    Object.keys(imgFiles).map(async (filePath) => {
      const fileName = filePath.split('/').pop();
      const imgSrc = `/img/marker/providers/${fileName}`;
      const image = await maplibreMap.loadImage(imgSrc);
      maplibreMap.addImage(fileName.replace(/\.[^/.]+$/, ''), image.data);
    }),
  );
}

function getPopupAnchor(lngLat, map) {
  const screenPoint = map.project(lngLat);
  const padding = map.getPadding(); // Get current map padding
  const canvasWidth = map.getCanvas().clientWidth;
  const canvasHeight = map.getCanvas().clientHeight;

  // Approximate popup dimensions
  const popupWidth = Math.min(400, canvasWidth * 0.5);
  const popupHeight = Math.min(350, canvasHeight * 0.5);

  // Space available around the point (adjusted for padding)
  const spaceLeft = screenPoint.x - padding.left;
  const spaceRight = canvasWidth - screenPoint.x - padding.right;
  const spaceTop = screenPoint.y - padding.top;
  const spaceBottom = canvasHeight - screenPoint.y - padding.bottom;

  let anchor = 'bottom'; // Default position

  if (
    spaceTop >= popupHeight
    && screenPoint.x + popupWidth / 2 <= canvasWidth - padding.right
    && screenPoint.x - popupWidth / 2 >= padding.left
  ) {
    anchor = 'bottom';
  } else if (
    spaceBottom >= popupHeight
    && screenPoint.x + popupWidth / 2 <= canvasWidth - padding.right
    && screenPoint.x - popupWidth / 2 >= padding.left
  ) {
    anchor = 'top';
  } else if (
    spaceLeft >= popupWidth
    && screenPoint.y + popupHeight / 2 <= canvasHeight - padding.bottom
    && screenPoint.y - popupHeight / 2 >= padding.top
  ) {
    anchor = 'right';
  } else if (
    spaceRight >= popupWidth
    && screenPoint.y + popupHeight / 2 <= canvasHeight - padding.bottom
    && screenPoint.y - popupHeight / 2 >= padding.top
  ) {
    anchor = 'left';
  } else if (spaceBottom >= popupHeight / 2 && spaceLeft >= popupWidth / 2) {
    anchor = 'top-right';
  } else if (spaceBottom >= popupHeight / 2 && spaceRight >= popupWidth / 2) {
    anchor = 'top-left';
  } else if (spaceTop >= popupHeight / 2 && spaceLeft >= popupWidth / 2) {
    anchor = 'bottom-right';
  } else if (spaceTop >= popupHeight / 2 && spaceRight >= popupWidth / 2) {
    anchor = 'bottom-left';
  }

  return anchor;
}

function createPopup(f, e, maptilersdk, map) {
  const lngLat = e?.lngLat || f.geometry.coordinates;
  const anchor = getPopupAnchor(lngLat, map);

  const popup = new maptilersdk.Popup({
    anchor,
    offset: {
      top: [0, 8],
      'top-left': [24, 8],
      'top-right': [-24, 8],
      bottom: [0, -48],
      'bottom-left': [24, -48],
      'bottom-right': [-24, -48],
      left: [24, 0],
      right: [-24, 0],
      center: [0, 0],
    },
  });
  const iconEl = document.createElement('div');
  const root = createRoot(iconEl);
  const popupNode = (
    <QueryClientProvider client={queryClient}>
      <TranslationsProvider translator={translator}>
        <Popup
          feature={f}
          onRemove={() => {
            popup.remove();
            root.unmount();
          }}
        />
      </TranslationsProvider>
    </QueryClientProvider>
  );
  root.render(popupNode);

  return {
    popup: popup
      .setMaxWidth('500px')
      .setDOMContent(iconEl)
      .setLngLat(lngLat),
    root,
  };
}

const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

function getInitialCenterAndZoom() {
  const { geolocation } = document.documentElement.dataset;

  const parsedGeolocation = JSON.parse(geolocation || {});

  if (!parsedGeolocation.lat || !parsedGeolocation.lng) {
    return {
      center: [4.5, 50.5],
      zoom: 7,
    };
  }

  return {
    center: [parsedGeolocation.lng, parsedGeolocation.lat],
    zoom: 13,
  };
}

const moveLayers = (mapRef) => {
  if (!mapRef.current) return;

  const map = mapRef.current;
  const { layers } = map.getStyle();

  if (!layers) return;

  const matchingLayers = layers
    .filter(layer => layer.id.startsWith('mtlr-gc'))
    .map(layer => layer.id);

  matchingLayers.forEach((layerId) => {
    if (map.getLayer(layerId) && map.getLayer('vehicles-clusters')) {
      map.moveLayer(layerId, 'vehicles-clusters');
    }
  });
};

export {
  addMarkerImagesToMap,
  getPopupAnchor,
  createPopup,
  getInitialCenterAndZoom,
  isEqual,
  moveLayers,
};